import { Button, Card, TextInput } from 'nhsuk-react-components';
import { ZodError } from 'zod';
import { ChangeEvent, useState } from 'react';
import {
  CampaignConfig, CampaignManagementDetails, campaignManagementFormSchema,
} from 'schemas/CampaignConfig.schema.ts';
import PointsList from 'components/forms/CampaignManagementDetails/PointsList.tsx';
import CampaignPageHeader from 'components/CampaignPageHeader/index.tsx';
import { CampaignTemplate } from 'schemas/CampaignTemplates.schema.ts';

import './CampaignManagement.scss';

type CampaignManagementFormDetails = {
  Manager: string,
  Approver: string,
  Reviewer: string,
}

export type CampaignManagementFormProps = {
  onSuccessfulSubmission: (managementDetails: CampaignManagementDetails) => void;
  isSubmitting?: boolean;
  campaign: CampaignConfig;
  template?: CampaignTemplate,
}

const parseInitialFormValues = (campaign: CampaignConfig): CampaignManagementFormDetails => ({
  Approver: campaign.CampaignConfig?.Approver?.join(',') || '',
  Manager: campaign.CampaignConfig?.Manager?.join(',') || '',
  Reviewer: campaign.CampaignConfig?.Reviewer?.join(',') || '',
});

function CampaignManagementForm({
  onSuccessfulSubmission,
  isSubmitting = false,
  campaign,
  template,
}: CampaignManagementFormProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formValues, setFormValues] = useState<CampaignManagementFormDetails>(parseInitialFormValues(campaign));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (errors[e.target.name]) {
      const clearedErrors = { ...errors };
      delete clearedErrors[e.target.name];
      setErrors(clearedErrors);
    }

    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const onSubmit = () => {
    try {
      setErrors({});

      const emailArrays: CampaignManagementDetails = {
        Manager: formValues.Manager.split(','),
        Approver: formValues.Approver.split(','),
        Reviewer: formValues.Reviewer.split(','),
      };

      campaignManagementFormSchema.parse(emailArrays);

      onSuccessfulSubmission(emailArrays);
    } catch (e) {
      if (e instanceof ZodError) {
        setErrors(Object.fromEntries(
          e.issues.map(({ path, message }) => [path[0].toString(), message]),
        ));
      }
    }
  };

  return (
    <>
      <CampaignPageHeader campaign={campaign} template={template} />
      <Card>
        <>
          <h3>Campaign contact details</h3>
          <p>
            You can enter more than one email address in each field, by separating
            with a comma. For example, &apos;person@nhs.net,person@nhs.net&apos;.
          </p>

          {
            template?.['CampaignConfig.Manager.input.display'] !== 'hidden' && (
              <>
                <div className="text-input">
                  <TextInput
                    id="campaign-manager"
                    label="Campaign manager"
                    name="Manager"
                    onChange={handleChange}
                    value={formValues.Manager}
                    error={errors.Manager}
                  />
                </div>

                <PointsList
                  header="Notified when:"
                  points={[
                    'a campaign is successfully set up or if the setup fails',
                    'a summary report is ready for review',
                    'a campaign is approved or cancelled',
                  ]}
                />
              </>
            )
          }

          {
            template?.['CampaignConfig.Reviewer.input.display'] !== 'hidden' && (
              <div className="nhsuk-u-margin-top-6 text-input">
                <TextInput
                  id="campaign-reviewer"
                  label="Campaign reviewer"
                  name="Reviewer"
                  onChange={handleChange}
                  value={formValues.Reviewer}
                  error={errors.Reviewer}
                />
                <PointsList
                  header="Notified when:"
                  points={[
                    'a summary report is ready for review',
                    'a campaign is approved or cancelled',
                  ]}
                />
              </div>
            )
          }
          {
            template?.['CampaignConfig.Approver.input.display'] !== 'hidden' && (
            <div className="nhsuk-u-margin-top-6 text-input">
              <TextInput
                id="campaign-approver"
                label="Campaign approver"
                name="Approver"
                onChange={handleChange}
                value={formValues.Approver}
                error={errors.Approver}
              />
              <PointsList
                header="Notified when:"
                points={[
                  'a summary report is ready for review',
                  'a campaign is approved or cancelled',
                ]}
              />
            </div>
            )
          }
        </>
      </Card>
      <Button onClick={onSubmit} disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Save and continue'}
      </Button>
    </>
  );
}

export default CampaignManagementForm;
